import calculateCarbonData from 'src/lib/carbondata/calculator';
import {
  BUY, BUYER, DIRECTIONS,
  METER, SELLER, TIME_ZONE_SYSTEM,
  TRADE, TRADE_DIRECTIONS_LABEL,
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { getCounterFactualValueFromOrderedResidualRules } from 'src/util/counterfactual';
import {
  extractData, filterRulesByTradePointId,
  getCounterfactualAvailability, getMemberNodes, getTimeStamp, getUntradedVolume,
  toBigEnergyValue,
} from 'src/util/mainDataBuilder';
import { measureSince, nowMs } from 'src/util/perfMarks';

/**
 * Prepares a map of property members
 * @param {object} propertyMembers
 * @returns {object} - map of property members.
 */
export const getPropertyMembers = (propertyMembers) => {
  const members = {};
  const properties = {};
  if (!propertyMembers) {
    return { members, properties };
  }
  propertyMembers?.forEach((member) => {
    const { uuid } = member || {};
    members[uuid] = { ...member, propertyId: member.property?.uuid };
    // eslint-disable-next-line no-unused-vars
    const { property, ...remainingAttrs } = members[uuid] || {};
    if (members[uuid]) members[uuid] = remainingAttrs;
    if (property?.uuid) properties[property?.uuid] = property;
  });
  return { members, properties };
};

/**
 * Prepares a map of portfolio user members
 * @param {object} userMembers
 * @returns {object} - map of portfolio user members.
 */
export const getUserMembers = (userMembers) => {
  const members = {};
  const users = {};
  if (!userMembers) {
    return { members, users };
  }
  const memberNodes = userMembers.edges?.map((item) => (
    { ...item.node, userId: item.node.user?.uuid }));

  memberNodes?.forEach((member) => {
    const { uuid } = member || {};
    // eslint-disable-next-line no-unused-vars
    const { user, ...remainingAttrs } = member;
    if (uuid) members[uuid] = remainingAttrs;
    if (user?.uuid) users[user.uuid] = user;
  });
  return { members, users };
};

/**
 * Group trade summaries by the participating portfolio trade point.
 * @param {Array<object>} tradeSetSummary Trade summaries from the portfolio query.
 * @returns {object} Summaries keyed by trade point in their original order.
 */
export const getTradeSummariesByTradePoint = (tradeSetSummary) => {
  const summariesByTradePoint = {};
  tradeSetSummary?.forEach((tradeSummary) => {
    const tradePointIds = new Set();
    tradeSummary?.data?.forEach(({
      buyerTradePointIds = [], directions = [], sellerTradePointIds = [],
    } = {}) => {
      if (directions.length !== 1) return;
      if (directions[0] === TRADE_DIRECTION_BUY) {
        buyerTradePointIds.forEach((tradePointId) => tradePointIds.add(tradePointId));
      }
      if (directions[0] === TRADE_DIRECTION_SELL) {
        sellerTradePointIds.forEach((tradePointId) => tradePointIds.add(tradePointId));
      }
    });
    tradePointIds.forEach((tradePointId) => {
      if (!summariesByTradePoint[tradePointId]) summariesByTradePoint[tradePointId] = [];
      summariesByTradePoint[tradePointId].push(tradeSummary);
    });
  });
  return summariesByTradePoint;
};

/**
 * Index the first datum for each key, matching Array.filter(...)[0] semantics.
 * @param {Array<object>} data Data to index.
 * @param {Function} keyFunc Function returning an index key.
 * @returns {Map<*, object>} First datum keyed by the selected value.
 */
const getFirstDatumIndex = (data, keyFunc) => {
  const index = new Map();
  data?.forEach((datum) => {
    const key = keyFunc(datum);
    if (typeof key === 'number' && Number.isNaN(key)) return;
    if (!index.has(key)) index.set(key, datum);
  });
  return index;
};

/**
 * Prepare timestamp indexes without modifying extracted or query data.
 * @param {Array<object>} extractList Extracted meter data.
 * @returns {Array<object>} Extracted data paired with direction indexes.
 */
const getPreparedExtractList = (extractList) => extractList.map((datum) => {
  const directionIndexes = {};
  DIRECTIONS.forEach((direction) => {
    const { meter, trades = [] } = datum[direction] || {};
    const tradeList = trades || [];
    directionIndexes[direction] = {
      hasMeterData: Boolean(meter?.data),
      meterDataByTimestamp: getFirstDatumIndex(meter?.data, (item) => item?.timestamp),
      trades: tradeList.map((trade) => ({
        trade,
        hasTradeData: Boolean(trade?.data),
        tradeDataByTimestamp: getFirstDatumIndex(
          trade?.data,
          (item) => item?.range?.finish,
        ),
      })),
    };
  });
  return { datum, directionIndexes };
});

/**
 * Group residual rules by the actor trade point and order them once.
 * @param {object} rules Rules keyed by UUID.
 * @param {string} direction Portfolio direction.
 * @returns {object} Ordered residual rules keyed by trade point.
 */
const getResidualRulesByTradePoint = (rules, direction) => {
  const rulesByTradePoint = {};
  const trader = direction === BUY ? BUYER : SELLER;
  Object.values(rules).forEach((rule) => {
    if (rule?.tradeType !== TRADE_TYPE_RESIDUAL) return;
    const tradePointId = rule[trader]?.tradePoint?.uuid;
    if (!tradePointId) return;
    if (!rulesByTradePoint[tradePointId]) rulesByTradePoint[tradePointId] = [];
    rulesByTradePoint[tradePointId].push(rule);
  });
  Object.values(rulesByTradePoint).forEach((residualRules) => {
    residualRules.sort((a, b) => a.acceptedAt - b.acceptedAt);
  });
  return rulesByTradePoint;
};

/**
 * Get a cached display timestamp for an aggregation and raw timestamp.
 * @param {Map<string, Map<number, object>>} timestampCache Timestamp cache.
 * @param {string} aggregation ISO8601 aggregation.
 * @param {number} timestamp Raw timestamp.
 * @param {string} timezone Portfolio timezone.
 * @returns {object} Luxon DateTime used by output and carbon calculations.
 */
const getCachedTimestamp = (timestampCache, aggregation, timestamp, timezone) => {
  if (!timestampCache.has(aggregation)) timestampCache.set(aggregation, new Map());
  const aggregationCache = timestampCache.get(aggregation);
  if (!aggregationCache.has(timestamp)) {
    aggregationCache.set(timestamp, getTimeStamp(aggregation, timestamp, timezone));
  }
  return aggregationCache.get(timestamp);
};

const getDirectionDataEntry = (mainData, direction, finalTimestamp) => {
  const directionData = mainData[direction].data;
  if (!directionData[finalTimestamp]) {
    directionData[finalTimestamp] = {
      meterDataAggregates: {},
      tradeSetSummaries: {},
      untradedDataAggregates: {},
    };
  }
  return directionData[finalTimestamp];
};

const getTradeDatum = ({
  direction, meterId, propertyRegion, residualRules, timestamp, timestampDate,
  tradeDataByTimestamp, tradePointId,
}) => {
  const sourceDatum = tradeDataByTimestamp.get(timestamp);
  if (!sourceDatum) {
    return {
      value: 0, volume: 0, carbon: 0, counterfactual: NaN, meterId,
    };
  }
  const {
    directions, types, volume, ...sourceData
  } = sourceDatum;
  const tradeDirection = directions?.length === 1 ? directions[0] : '';
  const type = types?.length === 1 ? types[0] : '';
  const carbon = direction === BUY ? calculateCarbonData({
    dataType: TRADE,
    timestamp: timestampDate,
    energy: toBigEnergyValue(volume),
    propertyRegion,
    direction: tradeDirection,
    type,
  }) : 0;
  const counterfactual = type === TRADE_TYPE_RESIDUAL
    ? NaN : getCounterFactualValueFromOrderedResidualRules({
      direction: TRADE_DIRECTIONS_LABEL[direction],
      range: sourceDatum.range,
      tradePointId,
      volume,
    }, residualRules);
  return {
    ...sourceData,
    volume,
    direction: tradeDirection,
    type,
    meterId,
    tradePointId,
    carbon: Number(carbon),
    counterfactual,
  };
};

const getMeterDatum = ({
  direction, meterDataByTimestamp, propertyRegion, timestamp, timestampDate,
}) => {
  const meterDatum = meterDataByTimestamp.get(timestamp) || {};
  const { value } = meterDatum;
  const carbon = direction === BUY ? calculateCarbonData({
    dataType: METER,
    timestamp: timestampDate,
    energy: toBigEnergyValue(value),
    propertyRegion,
    direction: TRADE_DIRECTIONS_LABEL[direction],
  }) : 0;
  return { ...meterDatum, carbon: Number(carbon) };
};

const addUntradedData = (mainData, direction, residualRulesByTradePoint) => {
  const directionLabel = TRADE_DIRECTIONS_LABEL[direction];
  const directionData = mainData[direction].data;
  Object.entries(directionData).forEach(([timestamp, info]) => {
    const { meterDataAggregates, tradeSetSummaries } = info;
    const untradedDataByMeter = {};
    Object.entries(meterDataAggregates).forEach(([meterId, meter]) => {
      if (!meter) return;
      const volume = toBigEnergyValue(meter.value);
      untradedDataByMeter[meterId] = { volume };
    });
    Object.values(tradeSetSummaries).forEach((tradeSummary) => {
      const { meterId, volume } = tradeSummary || {};
      const meterData = mainData.meters[meterId];
      const untradedVolume = toBigEnergyValue(untradedDataByMeter[meterId]?.volume);
      if (meterData?.aggregation && meterDataAggregates[meterId] && untradedVolume.gt(0)) {
        untradedDataByMeter[meterId].volume = getUntradedVolume(
          untradedVolume,
          toBigEnergyValue(volume),
        );
      }
    });
    Object.entries(untradedDataByMeter).forEach(([meterId, untradedDatum]) => {
      const { propertyId, tradePointId } = mainData.meters[meterId] || {};
      const propertyRegion = mainData.properties[propertyId]?.publicHolidayRegion || null;
      // Clamp unconditionally: the metered aggregate itself (not just the traded
      // subtraction above) can be a small negative due to floating-point noise.
      const volume = getUntradedVolume(untradedDatum.volume);
      const counterfactual = volume.eq(0) ? 0 : getCounterFactualValueFromOrderedResidualRules({
        direction: directionLabel,
        range: { start: timestamp, finish: timestamp },
        tradePointId,
        volume,
      }, residualRulesByTradePoint[tradePointId] || []);
      const carbon = calculateCarbonData({
        dataType: TRADE,
        timestamp,
        energy: volume,
        direction: directionLabel,
        type: TRADE_TYPE_RESIDUAL,
        propertyRegion,
      });
      untradedDataByMeter[meterId] = { volume, carbon, value: counterfactual };
    });
    directionData[timestamp].untradedDataAggregates = untradedDataByMeter;
  });
};

/**
 * Build main data that is to be used in portfolio dashboard
 * @param {object} portfolio The portfolio of properties.
 * @param {string} timezone The timezone of the portfolio.
 * @returns {object} - main data
 */
export const buildMainData = (portfolio, timezone) => {
  const buildStart = nowMs();
  const {
    active, externalIdentifier, uuid, title,
    meters: metersConnection,
    tradeSetSummary,
    propertyMembers: propertyMembersConnection,
    userMembers: userMembersConnection,
  } = portfolio || {};

  const extractList = [];
  const summariesByTradePoint = getTradeSummariesByTradePoint(tradeSetSummary);
  const meterNodes = getMemberNodes(metersConnection) || [];
  meterNodes.forEach((meterNode) => {
    const { tradePointId } = meterNode || {};
    if (!tradePointId) return;
    extractList.push(extractData({
      ...meterNode,
      tradeSetSummary: summariesByTradePoint[tradePointId] || [],
    }));
  });

  const { members: userMembers, users } = getUserMembers(userMembersConnection);
  const propertyNodes = getMemberNodes(propertyMembersConnection) || [];
  const { members: propertyMembers, properties } = getPropertyMembers(propertyNodes);

  const mainData = {
    portfolio: {
      active,
      externalIdentifier,
      uuid,
      propertyMembers,
      title,
      userMembers,
    },
    buy: { rules: {}, data: {} },
    sell: { rules: {}, data: {} },
    meters: {},
    properties: {},
    users,
  };
  const timestamps = { buy: [], sell: [] };

  // form rules and extract timestamp list
  extractList.forEach((data) => {
    const {
      rules, tradePointId, buy, sell, meters,
    } = data;
    // eslint-disable-next-line no-unused-vars
    const { property, ...meterData } = meters || {};
    const propertyId = property?.uuid;
    buy.meter?.data?.forEach((datum) => timestamps.buy.push(datum?.timestamp));
    sell.meter?.data?.forEach((datum) => timestamps.sell.push(datum?.timestamp));
    Object.assign(
      mainData.buy.rules,
      filterRulesByTradePointId(rules, tradePointId, BUYER),
    );
    Object.assign(
      mainData.sell.rules,
      filterRulesByTradePointId(rules, tradePointId, SELLER),
    );
    mainData.meters[meters.uuid] = { ...meterData, propertyId };
    mainData.properties[propertyId] = properties[propertyId];
  });
  DIRECTIONS.forEach((direction) => {
    timestamps[direction] = [...new Set(timestamps[direction])];
  });

  const preparedExtractList = getPreparedExtractList(extractList);

  // looping over all the timestamps
  DIRECTIONS.forEach((direction) => {
    const residualRulesByTradePoint = getResidualRulesByTradePoint(
      mainData[direction].rules,
      direction,
    );
    const timestampCache = new Map();
    timestamps[direction].forEach((timestamp) => {
      preparedExtractList.forEach(({ datum, directionIndexes }) => {
        const {
          uuid: meterId, propertyId,
          tradePointId,
        } = datum?.meters || {};
        const propertyRegion = mainData.properties[propertyId]?.publicHolidayRegion;
        const { meter } = datum[direction] || {};
        const { aggregation } = meter || {};
        if (!aggregation) return;
        const timestampDate = getCachedTimestamp(
          timestampCache,
          aggregation,
          timestamp,
          timezone,
        );
        const dataEntry = getDirectionDataEntry(mainData, direction, timestampDate.ts);
        const {
          hasMeterData, meterDataByTimestamp, trades,
        } = directionIndexes[direction];

        // Add tradeset summary grouped by rule id
        trades.forEach(({ trade, hasTradeData, tradeDataByTimestamp }) => {
          const { ruleId } = trade?.key || {};
          if (!ruleId || !hasTradeData) return;
          const tradeDatum = getTradeDatum({
            direction,
            meterId,
            propertyRegion,
            residualRules: residualRulesByTradePoint[tradePointId] || [],
            timestamp,
            timestampDate,
            tradeDataByTimestamp,
            tradePointId,
          });
          dataEntry.tradeSetSummaries[ruleId] = tradeDatum;
        });

        // add meter data grouped by meter trade point id
        if (hasMeterData && meterId) {
          dataEntry.meterDataAggregates[meterId] = getMeterDatum({
            direction,
            meterDataByTimestamp,
            propertyRegion,
            timestamp,
            timestampDate,
          });
        }
      });
    });
    addUntradedData(mainData, direction, residualRulesByTradePoint);
  });

  mainData.counterfactualAvailability = getCounterfactualAvailability(mainData);
  mainData.timezone = timezone || TIME_ZONE_SYSTEM;
  if (buildStart !== null) {
    measureSince('portfolio-show:buildMainData', buildStart);
  }
  return mainData;
};
